import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * Cascading Indonesian address picker for the cart page.
 *
 * Provinsi > Kabupaten/Kota > Kecamatan > Kelurahan/Desa, backed by the static dataset served from
 * the block's `data-api-url`. Picking a kelurahan fills the kode pos, which is the whole point:
 * buyers guess it far more often than they know it.
 *
 * The selection is written to cart attributes (see blocks/id-address-picker.liquid for the keys) and,
 * once complete, pushed to the checkout as a delivery address so the buyer doesn't retype it.
 */

/**
 * @typedef {[kode: string, nama: string, shopifyProvinceCode: string]} ProvinceRow
 * @typedef {[kode: string, nama: string]} RegencyRow
 * @typedef {[kode: string, nama: string, kodePos: string]} VillageRow
 * @typedef {{ version: number, provinces: ProvinceRow[], regencies: RegencyRow[] }} RegionIndex
 * @typedef {{ kode: string, districts: Record<string, [nama: string, villages: VillageRow[]]> }} RegencyShard
 */

/**
 * @typedef {object} Selection
 * @property {string} kode - Kelurahan kode, or a prefix of one while the cascade is incomplete.
 * @property {string} street
 */

const COUNTRY_CODE = 'ID';
const STOREFRONT_API_VERSION = '2025-07';

/**
 * Kode length at each level of the cascade. A kode is its own ancestry — '31.71.001.1001' contains
 * the province ('31'), regency ('31.71') and district ('31.71.001') — so truncating to the previous
 * entry is what "reset everything below this level" means.
 *
 * These widths are set by the emitter, not chosen here: see the Codes section in
 * id-address-api/scripts/build-data.mjs. Read them from the constants below rather than writing the
 * numbers inline — when the district segment went from 2 digits to 3, every hardcoded `slice(0, 8)`
 * silently started truncating a district kode to a prefix that matched nothing, and the kelurahan
 * dropdown came up empty with no error.
 */
const KODE_LENGTHS = /** @type {const} */ ([2, 5, 9, 14]);
const [PROVINCE_LENGTH, REGENCY_LENGTH, DISTRICT_LENGTH, VILLAGE_LENGTH] = KODE_LENGTHS;

/** Indonesian collation, so "É" and friends in kelurahan names sort where a buyer expects. */
const collator = new Intl.Collator('id');

/**
 * Shared across every instance and across re-renders — the index is ~18 KB and each shard is
 * fetched at most once per session no matter how often the cart section morphs.
 *
 * @type {Map<string, Promise<any>>}
 */
const requests = new Map();

/**
 * @param {string} url
 * @returns {Promise<any>}
 */
function loadJSON(url) {
  const pending = requests.get(url);
  if (pending) return pending;

  const request = fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
    if (!response.ok) throw new Error(`${url}: ${response.status}`);
    return response.json();
  });

  // A failed load must not be cached, or a blip would lock the buyer out of the form for the
  // rest of the session.
  request.catch(() => requests.delete(url));
  requests.set(url, request);

  return request;
}

/** @param {string} kode */
function provinceOf(kode) {
  return kode.slice(0, PROVINCE_LENGTH);
}

/** @param {string} kode */
function regencyOf(kode) {
  return kode.slice(0, REGENCY_LENGTH);
}

/** @param {string} kode */
function districtOf(kode) {
  return kode.slice(0, DISTRICT_LENGTH);
}

/**
 * Sorted on the name as-is. The dataset carries bare names at every level — "Bekasi", not
 * "Kabupaten Bekasi" or "Kota Bekasi", with the two merged into one entry upstream — so there is no
 * prefix to look past, and stripping one would only bury "Kepulauan Seribu" under S.
 *
 * @param {[string, string][]} rows
 * @returns {[string, string][]}
 */
function sortByName(rows) {
  return [...rows].sort((a, b) => collator.compare(a[1], b[1]));
}

/**
 * Rebuilds a select's options, preserving the placeholder rendered by Liquid.
 *
 * @param {HTMLSelectElement} select
 * @param {[string, string][]} options
 * @param {string} value - Kode to select, or '' for the placeholder.
 */
function setOptions(select, options, value) {
  const first = select.options[0];
  const placeholder = first && first.value === '' ? first : new Option('', '');
  const fragment = document.createDocumentFragment();

  fragment.append(placeholder);
  for (const [kode, nama] of options) {
    fragment.append(new Option(nama, kode, false, kode === value));
  }

  select.replaceChildren(fragment);
  select.value = options.some(([kode]) => kode === value) ? value : '';
  select.disabled = options.length === 0;
}

/**
 * @extends {Component<{
 *   street: HTMLInputElement,
 *   province: HTMLSelectElement,
 *   regency: HTMLSelectElement,
 *   district: HTMLSelectElement,
 *   village: HTMLSelectElement,
 *   postal: HTMLElement,
 *   status: HTMLElement,
 * }>}
 */
class IdAddressPicker extends Component {
  // Deliberately no `requiredRefs`: the block renders the element with no fields at all when the
  // merchant hasn't set a data URL yet, and throwing there would break the whole cart page.

  #baseUrl = '';

  /**
   * The in-memory selection. Authoritative over the server-rendered markup, because a cart section
   * morph can arrive carrying attributes from before the buyer's most recent pick.
   *
   * @type {Selection}
   */
  #selection = { kode: '', street: '' };

  /** @type {RegionIndex | null} */
  #index = null;

  /** @type {RegencyShard | null} */
  #shard = null;

  /**
   * Whether `#selection.kode` still has to pass the dataset-version check below.
   *
   * Only a kode restored from `data-selected-kode` needs it. A kode the buyer just picked came from
   * options built off the index that is loaded right now, so it is current by construction —
   * checking it anyway is what made the cascade look dead: on a cart with no saved address
   * `data-selected-version` is empty, never matches, and every first pick was discarded as stale
   * before the kabupaten options were built.
   */
  #selectionNeedsVersionCheck = false;

  /**
   * Guards against a slow shard fetch landing after the buyer has moved on. Aborting isn't an
   * option — the fetches are shared through `requests`, so cancelling one would cancel it for
   * everyone.
   */
  #renderId = 0;

  /** @type {AbortController | null} */
  #cartUpdate = null;

  connectedCallback() {
    super.connectedCallback();

    this.#baseUrl = (this.dataset.apiUrl ?? '').trim().replace(/\/+$/, '');
    if (!this.#baseUrl || !this.refs.province) return;

    this.#selection = {
      kode: this.dataset.selectedKode ?? '',
      street: this.refs.street?.value ?? '',
    };
    this.#selectionNeedsVersionCheck = Boolean(this.#selection.kode);

    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.#baseUrl || !this.refs.province) return;

    // The cart section re-renders on every quantity change. The morphed markup only carries the
    // saved selection, so the option lists have to be rebuilt and any in-flight edit restored.
    if (this.refs.street && this.refs.street.value !== this.#selection.street) {
      this.refs.street.value = this.#selection.street;
    }

    this.#render();
  }

  // No `disconnectedCallback` override: a pending save has to be allowed to land. The cart section
  // morphs rather than replaces, so this element normally stays connected anyway.

  /**
   * @param {string} url
   * @returns {string}
   */
  #url(url) {
    return `${this.#baseUrl}${url}`;
  }

  /**
   * Below province level a kode is a position within its parent, so a rebuild that adds or merges a
   * regency makes every later kode point somewhere else. Drops a *restored* kode whose dataset
   * version isn't the one the API is now serving, rather than quietly showing the buyer a different
   * kabupaten under the name they picked. Runs once per restored kode — see
   * `#selectionNeedsVersionCheck`; a kode the buyer picked in this session is never subject to it.
   *
   * A kode saved before this attribute existed carries no version, so it counts as stale too. The
   * cart keeps its old attributes until the next save — but with the selects empty, the block's
   * own completeness check won't let the buyer submit on them.
   *
   * @param {number} version - `version` from the index the options are about to be built from.
   */
  #discardStaleSelection(version) {
    if (!this.#selection.kode) return;
    if (this.dataset.selectedVersion === String(version)) return;

    this.#selection.kode = '';
    delete this.dataset.selectedKode;
  }

  /**
   * Brings all four selects in line with `#selection.kode`, which may be a partial kode ('', '31',
   * '31.71', '31.71.001') while the buyer is still working down the cascade. Every change routes
   * through here so there is one place that decides what each select contains.
   */
  async #render() {
    const { province, regency, district, village, postal } = this.refs;
    if (!province || !regency || !district || !village) return;

    const renderId = ++this.#renderId;

    try {
      this.#index ??= /** @type {RegionIndex} */ (await loadJSON(this.#url('/v1/index.json')));
      if (renderId !== this.#renderId) return;

      const index = this.#index;
      if (this.#selectionNeedsVersionCheck) {
        this.#selectionNeedsVersionCheck = false;
        this.#discardStaleSelection(index.version);
      }

      // Read after the staleness check, which may have cleared it.
      const { kode } = this.#selection;
      const provinceKode = provinceOf(kode);

      setOptions(
        province,
        sortByName(index.provinces.map(([value, nama]) => /** @type {[string, string]} */ ([value, nama]))),
        provinceKode
      );

      setOptions(
        regency,
        provinceKode ? sortByName(index.regencies.filter(([value]) => provinceOf(value) === provinceKode)) : [],
        regencyOf(kode)
      );

      const regencyKode = kode.length >= REGENCY_LENGTH ? regencyOf(kode) : '';
      if (!regencyKode) {
        setOptions(district, [], '');
        setOptions(village, [], '');
        if (postal) postal.textContent = '';
        return;
      }

      this.#setStatus('');
      const shard = /** @type {RegencyShard} */ (await loadJSON(this.#url(`/v1/regency/${regencyKode}.json`)));
      if (renderId !== this.#renderId) return;
      this.#shard = shard;

      setOptions(
        district,
        sortByName(
          Object.entries(shard.districts).map(([value, [nama]]) => /** @type {[string, string]} */ ([value, nama]))
        ),
        kode.length >= DISTRICT_LENGTH ? districtOf(kode) : ''
      );

      const villages = kode.length >= DISTRICT_LENGTH ? (shard.districts[districtOf(kode)]?.[1] ?? []) : [];
      setOptions(
        village,
        sortByName(villages.map(([value, nama]) => /** @type {[string, string]} */ ([value, nama]))),
        kode.length === VILLAGE_LENGTH ? kode : ''
      );

      if (postal) postal.textContent = this.#kodePos();
    } catch (error) {
      if (renderId !== this.#renderId) return;
      this.#setStatus(this.dataset.errorMessage ?? '', 'error');
      console.error(error);
    }
  }

  /** @returns {string} The kode pos of the selected kelurahan, or '' if the cascade is incomplete. */
  #kodePos() {
    const { kode } = this.#selection;
    if (kode.length !== VILLAGE_LENGTH) return '';

    const villages = this.#shard?.districts[districtOf(kode)]?.[1] ?? [];
    return villages.find(([value]) => value === kode)?.[2] ?? '';
  }

  /**
   * @param {string} message
   * @param {'error' | ''} [state]
   */
  #setStatus(message, state = '') {
    const { status } = this.refs;
    if (!status) return;

    status.textContent = message;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  /** @param {Event} event */
  selectProvince = (event) => {
    this.#select(event, 0);
  };

  /** @param {Event} event */
  selectRegency = (event) => {
    this.#select(event, 1);
  };

  /** @param {Event} event */
  selectDistrict = (event) => {
    this.#select(event, 2);
  };

  /** @param {Event} event */
  selectVillage = (event) => {
    this.#select(event, 3);
  };

  /**
   * @param {Event} event
   * @param {number} level - Index into `KODE_LENGTHS` of the level this select owns.
   */
  #select(event, level) {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement)) return;

    // Clearing a select falls back to its parent's kode, which drops this level and everything
    // below it in one step.
    this.#selection.kode = target.value
      ? target.value.slice(0, KODE_LENGTHS[level])
      : this.#selection.kode.slice(0, KODE_LENGTHS[level - 1] ?? 0);

    this.#render();
    this.saveAddress();
  }

  /**
   * Records every keystroke immediately, even though the save is debounced — a cart re-render can
   * land mid-word, and `updatedCallback` restores the field from `#selection`.
   *
   * @param {Event} event
   */
  updateStreet = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    this.#selection.street = target.value;
    this.saveAddress();
  };

  /**
   * Writes the address to the cart. Debounced because the street field fires on every keystroke,
   * and single-flight because only the newest state matters.
   */
  saveAddress = debounce(async () => {
    this.#cartUpdate?.abort();
    const abortController = new AbortController();
    this.#cartUpdate = abortController;

    const { kode } = this.#selection;
    const names = this.#names();
    const attributes = {
      // Stored even when partial, so a buyer who picks a province and reloads doesn't start over.
      // The kode's own length says how far down the cascade they got.
      _id_wilayah_kode: kode,
      // Stamped alongside it so a later visit can tell whether the kode still means what it did.
      _id_data_version: kode ? String(this.#index?.version ?? '') : '',
      _id_province_code: names.provinceCode,
      Alamat: this.#selection.street,
      Provinsi: names.province,
      'Kota/Kabupaten': names.regency,
      Kecamatan: names.district,
      Kelurahan: names.village,
      'Kode pos': this.#kodePos(),
    };

    try {
      const config = fetchConfig('json', { body: JSON.stringify({ attributes }) });
      const response = await fetch(Theme.routes.cart_update_url, { ...config, signal: abortController.signal });
      if (!response.ok) throw new Error(`cart update: ${response.status}`);

      this.dataset.selectedKode = attributes._id_wilayah_kode;
      this.dataset.selectedVersion = attributes._id_data_version;
      this.#prefillCheckout(attributes);
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error(error);
    } finally {
      if (this.#cartUpdate === abortController) this.#cartUpdate = null;
    }
  }, 300);

  /**
   * Resolves the current kode to the display names the merchant will read on the order.
   *
   * @returns {{ province: string, provinceCode: string, regency: string, district: string, village: string }}
   */
  #names() {
    const { kode } = this.#selection;
    const empty = { province: '', provinceCode: '', regency: '', district: '', village: '' };
    if (!kode || !this.#index) return empty;

    const province = this.#index.provinces.find(([value]) => value === provinceOf(kode));
    const regency =
      kode.length >= REGENCY_LENGTH ? this.#index.regencies.find(([value]) => value === regencyOf(kode)) : undefined;
    const district = kode.length >= DISTRICT_LENGTH ? this.#shard?.districts[districtOf(kode)] : undefined;
    const village = kode.length === VILLAGE_LENGTH ? district?.[1].find(([value]) => value === kode) : undefined;

    return {
      province: province?.[1] ?? '',
      provinceCode: province?.[2] ?? '',
      regency: regency?.[1] ?? '',
      district: district?.[0] ?? '',
      village: village?.[1] ?? '',
    };
  }

  /**
   * Pushes the completed address to the checkout so the buyer doesn't retype it.
   *
   * Best-effort by design: the cart attributes are already saved by the time this runs, so a
   * failure here costs the buyer some typing, not the order. Uses tokenless Storefront API access,
   * which the Online Store grants for cart read/write on same-origin requests.
   *
   * `cartDeliveryAddressesAdd` only ever appends — a second call after an earlier save left two
   * addresses on the cart, and checkout kept showing whichever one it saw first, so an edited
   * address silently stopped reaching the buyer. The cart attribute `_id_checkout_address_id`
   * remembers the address this component created so a later save updates it in place instead.
   *
   * @param {Record<string, string>} attributes
   */
  async #prefillCheckout(attributes) {
    if (attributes._id_wilayah_kode.length !== VILLAGE_LENGTH || !attributes.Alamat || !attributes._id_province_code)
      return;

    try {
      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      if (!cart.token) return;

      const cartId = `gid://shopify/Cart/${cart.token}`;
      const deliveryAddress = {
        address1: attributes.Alamat,
        address2: `${attributes.Kelurahan}, ${attributes.Kecamatan}`,
        city: attributes['Kota/Kabupaten'],
        provinceCode: attributes._id_province_code,
        countryCode: COUNTRY_CODE,
        zip: attributes['Kode pos'],
      };

      const existingId = cart.attributes?.['_id_checkout_address_id'];
      let addressId = existingId ? await this.#updateCheckoutAddress(cartId, existingId, deliveryAddress) : null;

      // No id yet, or the stored one no longer exists on this cart (e.g. a completed order
      // started a fresh one) — either way, there's nothing to update, so create one.
      if (!addressId) {
        addressId = await this.#addCheckoutAddress(cartId, deliveryAddress);
      }

      if (addressId && addressId !== existingId) {
        const config = fetchConfig('json', {
          body: JSON.stringify({ attributes: { _id_checkout_address_id: addressId } }),
        });
        await fetch(Theme.routes.cart_update_url, config);
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * @param {string} cartId
   * @param {string} addressId - A previously created CartSelectableAddress id.
   * @param {Record<string, string>} deliveryAddress
   * @returns {Promise<string | null>} `addressId` again on success, or null if it no longer exists.
   */
  async #updateCheckoutAddress(cartId, addressId, deliveryAddress) {
    const result = await this.#storefrontQuery(
      `mutation IdAddressUpdate($cartId: ID!, $addresses: [CartSelectableAddressUpdateInput!]!) {
        cartDeliveryAddressesUpdate(cartId: $cartId, addresses: $addresses) {
          userErrors { field message }
        }
      }`,
      {
        cartId,
        addresses: [{ id: addressId, selected: true, oneTimeUse: true, address: { deliveryAddress } }],
      }
    );

    const errors = result?.data?.cartDeliveryAddressesUpdate?.userErrors;
    return errors?.length ? null : addressId;
  }

  /**
   * @param {string} cartId
   * @param {Record<string, string>} deliveryAddress
   * @returns {Promise<string | null>}
   */
  async #addCheckoutAddress(cartId, deliveryAddress) {
    const result = await this.#storefrontQuery(
      `mutation IdAddressAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
        cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
          cart { delivery { addresses { id } } }
          userErrors { field message }
        }
      }`,
      {
        cartId,
        // The buyer picked this on the cart page, not in their address book — don't persist it
        // to their account behind their back.
        addresses: [{ selected: true, oneTimeUse: true, address: { deliveryAddress } }],
      }
    );

    const payload = result?.data?.cartDeliveryAddressesAdd;
    if (payload?.userErrors?.length) return null;

    // On a cart with no prior delivery addresses (the common case) this is also the only entry;
    // Shopify appends rather than reorders, so the last one is reliably the one just added.
    const addresses = payload?.cart?.delivery?.addresses ?? [];
    return addresses.at(-1)?.id ?? null;
  }

  /**
   * @param {string} query
   * @param {Record<string, unknown>} variables
   * @returns {Promise<any | null>}
   */
  async #storefrontQuery(query, variables) {
    const response = await fetch(`/api/${STOREFRONT_API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });
    return response.ok ? response.json() : null;
  }
}

if (!customElements.get('id-address-picker')) {
  customElements.define('id-address-picker', IdAddressPicker);
}
